import React from "react";

import { mdiPencil } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../../api/FastAPI";
import TextInput from "../../form/TextInput";
import Modal from "../../modal/Modal";

import type {
  GetSchedulesResponse,
  OVPBroadcast,
} from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface IEditOVPBroadcastModal {
  isOpen: boolean;
  onClose: () => void;
  broadcast: OVPBroadcast;
}

interface OVPBroadcastFormValues {
  name: string;
  broadcast_url: string;
  embed_url: string;
}

const FORM_ID = "edit-ovp-broadcast-form";

function ModalHeader({
  broadcastName,
}: {
  broadcastName: string;
}): React.JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="border-border flex rounded-xl border p-3">
        <Icon path={mdiPencil} size={1} />
      </div>
      <div className="text-[1.171875rem] font-semibold">
        {t("editOVPBroadcast", { broadcastName })}
      </div>
    </div>
  );
}

function EditOVPBroadcastModal({
  isOpen,
  onClose,
  broadcast,
}: IEditOVPBroadcastModal): React.JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const queryClient = useQueryClient();

  const methods = useForm<OVPBroadcastFormValues>({
    defaultValues: {
      name: broadcast.name,
      broadcast_url: broadcast.broadcast_url ?? "",
      embed_url: broadcast.embed_url,
    },
  });

  const { queryKey: schedulesQueryKey } = FastAPI.generateQuery({
    fn: FastAPI.getSchedules,
    params: [{ channelId }],
  });

  const updateBroadcastMutation = useMutation({
    mutationFn: async (updatedBroadcast: OVPBroadcast) =>
      FastAPI.updateBroadcast(channelId, updatedBroadcast),
    onMutate: async (updatedBroadcast: OVPBroadcast) => {
      await queryClient.cancelQueries(schedulesQueryKey);

      const previousSchedules =
        queryClient.getQueryData<GetSchedulesResponse>(schedulesQueryKey);

      if (previousSchedules) {
        queryClient.setQueryData<GetSchedulesResponse>(schedulesQueryKey, {
          ...previousSchedules,
          data: previousSchedules.data.map((schedule) => ({
            ...schedule,
            broadcasts: schedule.broadcasts.map((broadcastItem) =>
              broadcastItem.uuid === updatedBroadcast.uuid
                ? updatedBroadcast
                : broadcastItem,
            ),
          })),
        });
      }

      return { previousSchedules };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSchedules) {
        queryClient.setQueryData(schedulesQueryKey, context.previousSchedules);
      }
      toast.error(t("editOVPBroadcastError"));
    },
    onSuccess: () => {
      toast.success(t("editOVPBroadcastSuccess"));
    },
    onSettled: () => {
      queryClient.invalidateQueries(schedulesQueryKey);
    },
  });

  const handleSubmit = (data: OVPBroadcastFormValues): void => {
    updateBroadcastMutation.mutate({ ...broadcast, ...data });
    onClose();
  };

  return (
    <Modal
      isDivided
      withDarkBackground
      header={<ModalHeader broadcastName={broadcast.name} />}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      actions={[
        {
          label: t("cancel", { ns: "common" }),
          buttonVariant: "secondary",
          isCloseAfterAction: true,
        },
        {
          label: t("save", { ns: "common" }),
          formId: FORM_ID,
          isCloseAfterAction: false,
        },
      ]}
    >
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form id={FORM_ID} onSubmit={methods.handleSubmit(handleSubmit)}>
          <TextInput
            isFullWidth
            label={t("editOVPBroadcastName")}
            name="name"
          />
          <TextInput
            isFullWidth
            label={t("editOVPBroadcastStreamUrl")}
            name="broadcast_url"
          />
          <TextInput
            isFullWidth
            label={t("editOVPBroadcastEmbedUrl")}
            name="embed_url"
          />
        </form>
      </FormProvider>
    </Modal>
  );
}

export default EditOVPBroadcastModal;
